// components/ReportesDemograficos.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Button, 
  Space, 
  Spin,
  Alert,
  Select,
  Badge,
  Typography,
  Divider,
  Progress,
  Empty,
  message,
  Tooltip,
  Tag
} from 'antd';
import {
  UserOutlined,
  EnvironmentOutlined,
  LineChartOutlined,
  TeamOutlined,
  CalendarOutlined,
  ManOutlined,
  WomanOutlined,
  HomeOutlined,
  RiseOutlined,
  FallOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReporteriaService from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../utils/exportExcel';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const ReportesDemograficos = () => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [distribucion, setDistribucion] = useState(null);
    const [topComunas, setTopComunas] = useState(null);
    const [analisisVecinos, setAnalisisVecinos] = useState(null);
    const [estadisticasEdad, setEstadisticasEdad] = useState(null);

    // Estados de carga individuales para cada botón CSV
    const [descargandoEdad, setDescargandoEdad] = useState(false);
    const [descargandoGenero, setDescargandoGenero] = useState(false);
    const [descargandoComunas, setDescargandoComunas] = useState(false);
    
    const [incluirSoloInscritos, setIncluirSoloInscritos] = useState(false);
    const [periodo, setPeriodo] = useState('todos');
    const [fechas, setFechas] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);

    // Params activos según filtros del usuario
    const getParamsActivos = () => ({
        incluirSoloInscritos,
        fechaInicio: incluirSoloInscritos && periodo !== 'todos'
            ? fechas[0].format('YYYY-MM-DD')
            : undefined,
        fechaFin: incluirSoloInscritos && periodo !== 'todos'
            ? fechas[1].format('YYYY-MM-DD')
            : undefined
    });

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const params = getParamsActivos();

            const [distrib, comunas, vecinos, edad] = await Promise.all([
                ReporteriaService.GetDistribucionDemografica(params),
                ReporteriaService.GetTopComunas({ limite: 10, incluirSoloInscritos }),
                ReporteriaService.GetAnalisisVecinos({ ...params, comunaReferencia: 'Ñuñoa' }),
                ReporteriaService.GetEstadisticasEdad({ incluirSoloInscritos })
            ]);

            setDistribucion(distrib);
            setTopComunas(comunas);
            setAnalisisVecinos(vecinos);
            setEstadisticasEdad(edad);
            
            message.success('Datos cargados exitosamente');
        } catch (error) {
            console.error('Error al cargar datos demográficos:', error);
            message.error('Error al cargar los datos. Por favor intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    // ── Contexto común de los reportes demográficos ───────────────────────────
    const filtrosDemografia = {
        'Universo': incluirSoloInscritos ? 'Solo usuarios inscritos' : 'Todos los usuarios',
        'Desde': fechas?.[0] ? fechas[0].format('DD/MM/YYYY') : null,
        'Hasta': fechas?.[1] ? fechas[1].format('DD/MM/YYYY') : null,
    };

    /** Columnas compartidas por las tres distribuciones. */
    const columnasDistribucion = (headerCategoria, keyCategoria) => ([
        { header: headerCategoria, key: keyCategoria, tipo: 'texto', ancho: 26 },
        { header: 'Cantidad', key: 'cantidad', tipo: 'numero', total: true },
        { header: '% del total', key: 'porcentaje', tipo: 'porcentaje' },
    ]);

    // ── Handlers de descarga ──────────────────────────────────────────────────
    const handleDescargarEdad = async () => {
        const datos = distribucion?.distribucionPorEdad;
        if (!datos?.length) return message.warning('No hay datos de edad para exportar');
        setDescargandoEdad(true);
        try {
            const est = estadisticasEdad?.estadisticas;
            await exportarLibro({
                archivo: 'demografia-edad',
                titulo: 'Distribución por edad',
                subtitulo: 'Reporte demográfico de usuarios',
                usuario: user,
                filtros: filtrosDemografia,
                hojas: [
                    // El panel muestra promedio, mediana, mínima y máxima; antes
                    // nada de eso llegaba al archivo descargado.
                    ...(est ? [{
                        nombre: 'Estadísticas de edad',
                        metricas: [
                            { metrica: 'Cobertura' },
                            { metrica: 'Usuarios con edad registrada', valor: est.total ?? 0, tipo: 'numero' },
                            { metrica: 'Edad' },
                            { metrica: 'Promedio', valor: est.promedio ?? 0, tipo: 'decimal' },
                            { metrica: 'Mediana', valor: est.mediana ?? 0, tipo: 'decimal' },
                            { metrica: 'Mínima', valor: est.minima ?? 0, tipo: 'numero' },
                            { metrica: 'Máxima', valor: est.maxima ?? 0, tipo: 'numero' },
                        ],
                    }] : []),
                    {
                        nombre: 'Por rango de edad',
                        columnas: columnasDistribucion('Rango de edad', 'rangoEdad'),
                        filas: datos.map(d => ({
                            rangoEdad: d.rangoEdad,
                            cantidad: d.cantidad,
                            porcentaje: d.porcentaje,
                        })),
                    },
                ],
            });
            message.success('Excel de edades descargado correctamente');
        } catch { message.error('Error al generar el archivo'); }
        finally { setDescargandoEdad(false); }
    };

    const handleDescargarGenero = async () => {
        const datos = distribucion?.distribucionPorGenero;
        if (!datos?.length) return message.warning('No hay datos de género para exportar');
        setDescargandoGenero(true);
        try {
            await exportarLibro({
                archivo: 'demografia-genero',
                titulo: 'Distribución por género',
                subtitulo: 'Reporte demográfico de usuarios',
                usuario: user,
                filtros: filtrosDemografia,
                hojas: [{
                    nombre: 'Por género',
                    columnas: columnasDistribucion('Género', 'genero'),
                    filas: datos.map(d => ({
                        genero: d.genero,
                        cantidad: d.cantidad,
                        porcentaje: d.porcentaje,
                    })),
                }],
            });
            message.success('Excel de género descargado correctamente');
        } catch { message.error('Error al generar el archivo'); }
        finally { setDescargandoGenero(false); }
    };

    const handleDescargarComunas = async () => {
        const datos = distribucion?.distribucionPorComuna;
        if (!datos?.length) return message.warning('No hay datos de comunas para exportar');
        setDescargandoComunas(true);
        try {
            const v = analisisVecinos?.resumen;
            await exportarLibro({
                archivo: 'demografia-comunas',
                titulo: 'Distribución por comuna',
                subtitulo: 'Reporte demográfico de usuarios',
                usuario: user,
                filtros: filtrosDemografia,
                hojas: [
                    {
                        nombre: 'Por comuna',
                        columnas: columnasDistribucion('Comuna', 'comuna'),
                        filas: datos.map(d => ({
                            comuna: d.comuna,
                            cantidad: d.cantidad,
                            porcentaje: d.porcentaje,
                        })),
                    },
                    // El análisis vecino/no vecino solo existía en pantalla.
                    ...(v ? [{
                        nombre: 'Vecinos vs. no vecinos',
                        columnas: [
                            { header: 'Segmento', key: 'segmento', tipo: 'texto', ancho: 28 },
                            { header: 'Usuarios', key: 'cantidad', tipo: 'numero', total: true },
                            { header: '% del total', key: 'porcentaje', tipo: 'porcentaje' },
                            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
                            { header: 'Ticket promedio', key: 'ticket', tipo: 'moneda' },
                            { header: 'Descuentos otorgados', key: 'descuentos', tipo: 'moneda', total: true },
                        ],
                        filas: [
                            {
                                segmento: `Vecinos de ${analisisVecinos.comunaReferencia}`,
                                cantidad: v.vecinos?.cantidad,
                                porcentaje: v.vecinos?.porcentaje,
                                ingresos: v.vecinos?.totalIngresos,
                                ticket: v.vecinos?.promedioTicket,
                                descuentos: v.vecinos?.totalDescuentos,
                            },
                            {
                                segmento: 'No vecinos',
                                cantidad: v.noVecinos?.cantidad,
                                porcentaje: v.noVecinos?.porcentaje,
                                ingresos: v.noVecinos?.totalIngresos,
                                ticket: v.noVecinos?.promedioTicket,
                                descuentos: v.noVecinos?.totalDescuentos,
                            },
                        ],
                        notas: [`Comuna de referencia: ${analisisVecinos.comunaReferencia}.`],
                    }] : []),
                ],
            });
            message.success('Excel de comunas descargado correctamente');
        } catch { message.error('Error al generar el archivo'); }
        finally { setDescargandoComunas(false); }
    };

    // ── Helpers de género ─────────────────────────────────────────────────────
    const getGenderColor = (genero) => {
        const g = genero?.toLowerCase();
        if (g === 'masculino' || g === 'hombre' || g === 'male') return '#0047bb';
        if (g === 'femenino' || g === 'mujer' || g === 'female') return '#eb2f96';
        return '#8c8c8c';
    };

    const getGenderIcon = (genero) => {
        const g = genero?.toLowerCase();
        if (g === 'masculino' || g === 'hombre' || g === 'male') return <ManOutlined />;
        if (g === 'femenino' || g === 'mujer' || g === 'female') return <WomanOutlined />;
        return <UserOutlined />;
    };

    const handlePeriodoChange = (value) => {
        setPeriodo(value);
        const hoy = dayjs();
        switch (value) {
            case 'mes_actual':
                setFechas([hoy.startOf('month'), hoy.endOf('month')]);
                break;
            case 'trimestre_actual':
                setFechas([hoy.startOf('quarter'), hoy.endOf('quarter')]);
                break;
            case 'año_actual':
                setFechas([hoy.startOf('year'), hoy.endOf('year')]);
                break;
            case 'ultimos_30_dias':
                setFechas([hoy.subtract(30, 'day'), hoy]);
                break;
            case 'ultimos_90_dias':
                setFechas([hoy.subtract(90, 'day'), hoy]);
                break;
            default:
                break;
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <Title level={2} className="mb-2">
                    <UserOutlined className="mr-2" />
                    Reportes Demográficos
                </Title>
                <Text type="secondary">
                    Análisis detallado del perfil demográfico de usuarios
                </Text>
            </div>

            {/* Filtros */}
            <Card className="mb-6 shadow-sm">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical" className="w-full">
                            <Text strong>
                                <FilterOutlined className="mr-2" />
                                Tipo de Usuario:
                            </Text>
                            <Select
                                value={incluirSoloInscritos ? 'inscritos' : 'todos'}
                                onChange={(value) => setIncluirSoloInscritos(value === 'inscritos')}
                                className="w-full"
                                size="large"
                            >
                                <Option value="todos">
                                    <TeamOutlined className="mr-2" />
                                    Todos los registrados
                                </Option>
                                <Option value="inscritos">
                                    <CheckCircleOutlined className="mr-2" />
                                    Solo inscritos
                                </Option>
                            </Select>
                        </Space>
                    </Col>

                    {incluirSoloInscritos && (
                        <>
                            <Col xs={24} sm={12} md={6}>
                                <Space direction="vertical" className="w-full">
                                    <Text strong>
                                        <CalendarOutlined className="mr-2" />
                                        Periodo:
                                    </Text>
                                    <Select
                                        value={periodo}
                                        onChange={handlePeriodoChange}
                                        className="w-full"
                                        size="large"
                                    >
                                        <Option value="todos">Todos los tiempos</Option>
                                        <Option value="mes_actual">Mes Actual</Option>
                                        <Option value="trimestre_actual">Trimestre Actual</Option>
                                        <Option value="año_actual">Año Actual</Option>
                                        <Option value="ultimos_30_dias">Últimos 30 días</Option>
                                        <Option value="ultimos_90_dias">Últimos 90 días</Option>
                                    </Select>
                                </Space>
                            </Col>

                            {periodo !== 'todos' && (
                                <Col xs={24} sm={12} md={8}>
                                    <Space direction="vertical" className="w-full">
                                        <Text strong>Rango Personalizado:</Text>
                                        <RangePicker
                                            value={fechas}
                                            onChange={(dates) => {
                                                if (dates) {
                                                    setFechas(dates);
                                                    setPeriodo('personalizado');
                                                }
                                            }}
                                            format="DD/MM/YYYY"
                                            className="w-full"
                                            size="large"
                                        />
                                    </Space>
                                </Col>
                            )}
                        </>
                    )}
                    
                    <Col xs={24} sm={12} md={4}>
                        <Space className="w-full mt-6">
                            <Button 
                                type="primary" 
                                size="large"
                                icon={<ReloadOutlined />}
                                onClick={cargarDatos}
                                loading={loading}
                                style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
                            >
                                Actualizar
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Spin size="large" tip="Cargando datos demográficos..." />
                </div>
            ) : !distribucion ? (
                <Alert
                    message="No hay datos disponibles"
                    description="Presiona el botón 'Actualizar' para cargar los datos demográficos."
                    type="info"
                    showIcon
                />
            ) : (
                <>
                    {/* KPIs Principales */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="shadow-sm hover:shadow-md transition-shadow">
                                <Statistic
                                    title={
                                        <span className="text-gray-600 font-medium">
                                            <TeamOutlined className="mr-2" />
                                            Total Usuarios
                                        </span>
                                    }
                                    value={distribucion.resumen.totalUsuarios}
                                    valueStyle={{ color: '#0047bb', fontSize: '32px', fontWeight: 'bold' }}
                                />
                                <Divider className="my-2" />
                                <Text type="secondary" className="text-xs">
                                    {incluirSoloInscritos ? 'Usuarios con inscripciones' : 'Usuarios registrados'}
                                </Text>
                            </Card>
                        </Col>
                        
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="shadow-sm hover:shadow-md transition-shadow">
                                <Statistic
                                    title={
                                        <span className="text-gray-600 font-medium">
                                            <LineChartOutlined className="mr-2" />
                                            Datos de Edad
                                        </span>
                                    }
                                    value={distribucion.resumen.porcentajeCompletoEdad}
                                    suffix="%"
                                    valueStyle={{ color: '#0047bb', fontSize: '32px', fontWeight: 'bold' }}
                                />
                                <Divider className="my-2" />
                                <Progress 
                                    percent={parseFloat(distribucion.resumen.porcentajeCompletoEdad)} 
                                    strokeColor="#0047bb"
                                    showInfo={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                        
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="shadow-sm hover:shadow-md transition-shadow">
                                <Statistic
                                    title={
                                        <span className="text-gray-600 font-medium">
                                            <UserOutlined className="mr-2" />
                                            Datos de Género
                                        </span>
                                    }
                                    value={distribucion.resumen.porcentajeCompletoGenero}
                                    suffix="%"
                                    valueStyle={{ color: '#fa8c16', fontSize: '32px', fontWeight: 'bold' }}
                                />
                                <Divider className="my-2" />
                                <Progress 
                                    percent={parseFloat(distribucion.resumen.porcentajeCompletoGenero)} 
                                    strokeColor="#fa8c16"
                                    showInfo={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                        
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="shadow-sm hover:shadow-md transition-shadow">
                                <Statistic
                                    title={
                                        <span className="text-gray-600 font-medium">
                                            <EnvironmentOutlined className="mr-2" />
                                            Datos de Comuna
                                        </span>
                                    }
                                    value={distribucion.resumen.porcentajeCompletoComuna}
                                    suffix="%"
                                    valueStyle={{ color: '#eb2f96', fontSize: '32px', fontWeight: 'bold' }}
                                />
                                <Divider className="my-2" />
                                <Progress 
                                    percent={parseFloat(distribucion.resumen.porcentajeCompletoComuna)} 
                                    strokeColor="#eb2f96"
                                    showInfo={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Estadísticas de Edad */}
                    {estadisticasEdad?.estadisticas && (
                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24}>
                                <Card 
                                    title={
                                        <span className="text-lg font-semibold">
                                            <LineChartOutlined className="mr-2 text-blue-500" />
                                            Estadísticas de Edad
                                        </span>
                                    }
                                    className="shadow-sm"
                                    extra={
                                        <Tag color="blue">
                                            {estadisticasEdad.estadisticas.total} usuarios con edad registrada
                                        </Tag>
                                    }
                                >
                                    <Row gutter={[16, 16]}>
                                        <Col xs={12} sm={6}>
                                            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                                                <Text type="secondary" className="text-xs block mb-2">Promedio</Text>
                                                <Text strong style={{ fontSize: '28px', color: '#0047bb' }}>
                                                    {estadisticasEdad.estadisticas.promedio}
                                                </Text>
                                                <Text type="secondary" className="text-xs block">años</Text>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                                                <Text type="secondary" className="text-xs block mb-2">Mediana</Text>
                                                <Text strong style={{ fontSize: '28px', color: '#0047bb' }}>
                                                    {estadisticasEdad.estadisticas.mediana}
                                                </Text>
                                                <Text type="secondary" className="text-xs block">años</Text>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                                                <Text type="secondary" className="text-xs block mb-2">Mínima</Text>
                                                <Text strong style={{ fontSize: '28px', color: '#fa8c16' }}>
                                                    {estadisticasEdad.estadisticas.minima}
                                                </Text>
                                                <Text type="secondary" className="text-xs block">años</Text>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <div className="text-center p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg">
                                                <Text type="secondary" className="text-xs block mb-2">Máxima</Text>
                                                <Text strong style={{ fontSize: '28px', color: '#eb2f96' }}>
                                                    {estadisticasEdad.estadisticas.maxima}
                                                </Text>
                                                <Text type="secondary" className="text-xs block">años</Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ── Distribución por Edad y Género ── */}
                    <Row gutter={[16, 16]} className="mb-6">

                        {/* Card Edad con botón CSV */}
                        <Col xs={24} lg={12}>
                            <Card 
                                title={
                                    <span className="text-lg font-semibold">
                                        <CalendarOutlined className="mr-2 text-blue-500" />
                                        Distribución por Edad
                                    </span>
                                }
                                className="shadow-sm"
                                style={{ height: '100%' }}
                                extra={
                                    <Tooltip title="Descargar distribución por edad en Excel">
                                        <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            loading={descargandoEdad}
                                            onClick={handleDescargarEdad}
                                            disabled={!distribucion?.distribucionPorEdad?.length}
                                        >
                                            CSV
                                        </Button>
                                    </Tooltip>
                                }
                            >
                                {distribucion.distribucionPorEdad?.length > 0 ? (
                                    <div className="space-y-3">
                                        {distribucion.distribucionPorEdad.map((item, index) => (
                                            <div key={index} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                                <div className="flex justify-between items-center mb-2">
                                                    <Text strong className="text-sm">{item.rangoEdad}</Text>
                                                    <div className="flex items-center space-x-2">
                                                        <Tag color="blue">{item.cantidad}</Tag>
                                                        <Badge 
                                                            count={`${item.porcentaje}%`} 
                                                            style={{ backgroundColor: '#0047bb' }}
                                                        />
                                                    </div>
                                                </div>
                                                <Progress 
                                                    percent={parseFloat(item.porcentaje)} 
                                                    strokeColor={{ '0%': '#0047bb', '100%': '#096dd9' }}
                                                    showInfo={false}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Empty description="No hay datos de edad disponibles" />
                                )}
                            </Card>
                        </Col>

                        {/* Card Género con botón CSV */}
                        <Col xs={24} lg={12}>
                            <Card 
                                title={
                                    <span className="text-lg font-semibold">
                                        <TeamOutlined className="mr-2 text-orange-500" />
                                        Distribución por Género
                                    </span>
                                }
                                className="shadow-sm"
                                style={{ height: '100%' }}
                                extra={
                                    <Tooltip title="Descargar distribución por género en Excel">
                                        <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            loading={descargandoGenero}
                                            onClick={handleDescargarGenero}
                                            disabled={!distribucion?.distribucionPorGenero?.length}
                                        >
                                            CSV
                                        </Button>
                                    </Tooltip>
                                }
                            >
                                {distribucion.distribucionPorGenero?.length > 0 ? (
                                    <div className="space-y-4">
                                        {distribucion.distribucionPorGenero.map((item, index) => (
                                            <div 
                                                key={index} 
                                                className="p-4 rounded-lg"
                                                style={{ 
                                                    backgroundColor: `${getGenderColor(item.genero)}15`,
                                                    border: `2px solid ${getGenderColor(item.genero)}30`
                                                }}
                                            >
                                                <div className="flex justify-between items-center mb-3">
                                                    <div className="flex items-center space-x-2">
                                                        <span style={{ fontSize: '24px', color: getGenderColor(item.genero) }}>
                                                            {getGenderIcon(item.genero)}
                                                        </span>
                                                        <Text strong style={{ fontSize: '16px' }}>
                                                            {item.genero}
                                                        </Text>
                                                    </div>
                                                    <div className="text-right">
                                                        <Text strong style={{ fontSize: '24px', color: getGenderColor(item.genero) }}>
                                                            {item.cantidad}
                                                        </Text>
                                                        <Text type="secondary" className="block text-xs">
                                                            {item.porcentaje}% del total
                                                        </Text>
                                                    </div>
                                                </div>
                                                <Progress 
                                                    percent={parseFloat(item.porcentaje)} 
                                                    strokeColor={getGenderColor(item.genero)}
                                                    strokeWidth={12}
                                                    showInfo={false}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Empty description="No hay datos de género disponibles" />
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Top Comunas con botón CSV ── */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24}>
                            <Card 
                                title={
                                    <span className="text-lg font-semibold">
                                        <EnvironmentOutlined className="mr-2 text-pink-500" />
                                        Top 10 Comunas
                                    </span>
                                }
                                className="shadow-sm"
                                extra={
                                    <Space>
                                        <Tag color="magenta">
                                            {distribucion.distribucionPorComuna?.length || 0} comunas registradas
                                        </Tag>
                                        <Tooltip title="Descargar todas las comunas en Excel">
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                loading={descargandoComunas}
                                                onClick={handleDescargarComunas}
                                                disabled={!distribucion?.distribucionPorComuna?.length}
                                            >
                                                CSV
                                            </Button>
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                {distribucion.distribucionPorComuna?.length > 0 ? (
                                    <Row gutter={[16, 16]}>
                                        {distribucion.distribucionPorComuna.slice(0, 10).map((item, index) => (
                                            <Col xs={24} sm={12} key={index}>
                                                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all">
                                                    <div className="flex items-center space-x-3">
                                                        <Badge 
                                                            count={index + 1} 
                                                            style={{ 
                                                                backgroundColor: index === 0 ? '#faad14' : 
                                                                                index === 1 ? '#d9d9d9' : 
                                                                                index === 2 ? '#cd7f32' : '#8c8c8c',
                                                                minWidth: '32px',
                                                                height: '32px',
                                                                lineHeight: '32px',
                                                                fontSize: '16px',
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                        <div>
                                                            <Text strong className="block">{item.comuna}</Text>
                                                            <Text type="secondary" className="text-xs">
                                                                {item.porcentaje}% del total
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <Text strong style={{ fontSize: '20px', color: '#eb2f96' }}>
                                                            {item.cantidad}
                                                        </Text>
                                                        <Text type="secondary" className="block text-xs">
                                                            usuarios
                                                        </Text>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <Empty description="No hay datos de comunas disponibles" />
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* Análisis de Vecinos */}
                    {analisisVecinos && (
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Card 
                                    title={
                                        <span className="text-lg font-semibold">
                                            <HomeOutlined className="mr-2 text-blue-500" />
                                            Análisis de Vecinos - {analisisVecinos.comunaReferencia}
                                        </span>
                                    }
                                    className="shadow-sm"
                                    extra={
                                        <Tooltip title="Comparación entre vecinos de Ñuñoa y usuarios de otras comunas">
                                            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                        </Tooltip>
                                    }
                                >
                                    <Row gutter={[24, 24]}>
                                        <Col xs={24} md={12}>
                                            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200">
                                                <div className="flex justify-between items-start mb-4">
                                                    <div>
                                                        <Text type="secondary" className="text-xs block mb-1">
                                                            Vecinos de {analisisVecinos.comunaReferencia}
                                                        </Text>
                                                        <Text strong style={{ fontSize: '36px', color: '#0047bb' }}>
                                                            {analisisVecinos.resumen.vecinos.cantidad}
                                                        </Text>
                                                    </div>
                                                    <Badge 
                                                        count={`${analisisVecinos.resumen.vecinos.porcentaje}%`}
                                                        style={{ 
                                                            backgroundColor: '#0047bb',
                                                            fontSize: '14px',
                                                            padding: '4px 12px',
                                                            height: 'auto'
                                                        }}
                                                    />
                                                </div>
                                                <Divider className="my-3" />
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Total Ingresos:</Text>
                                                        <Text strong style={{ color: '#0047bb', fontSize: '18px' }}>
                                                            ${analisisVecinos.resumen.vecinos.totalIngresos.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Ticket Promedio:</Text>
                                                        <Text strong>
                                                            ${analisisVecinos.resumen.vecinos.promedioTicket.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Total Descuentos:</Text>
                                                        <Text strong style={{ color: '#faad14' }}>
                                                            ${analisisVecinos.resumen.vecinos.totalDescuentos.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl border-2 border-blue-200">
                                                <div className="flex justify-between items-start mb-4">
                                                    <div>
                                                        <Text type="secondary" className="text-xs block mb-1">
                                                            Otras Comunas
                                                        </Text>
                                                        <Text strong style={{ fontSize: '36px', color: '#0047bb' }}>
                                                            {analisisVecinos.resumen.noVecinos.cantidad}
                                                        </Text>
                                                    </div>
                                                    <Badge 
                                                        count={`${analisisVecinos.resumen.noVecinos.porcentaje}%`}
                                                        style={{ 
                                                            backgroundColor: '#0047bb',
                                                            fontSize: '14px',
                                                            padding: '4px 12px',
                                                            height: 'auto'
                                                        }}
                                                    />
                                                </div>
                                                <Divider className="my-3" />
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Total Ingresos:</Text>
                                                        <Text strong style={{ color: '#0047bb', fontSize: '18px' }}>
                                                            ${analisisVecinos.resumen.noVecinos.totalIngresos.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Ticket Promedio:</Text>
                                                        <Text strong>
                                                            ${analisisVecinos.resumen.noVecinos.promedioTicket.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Total Descuentos:</Text>
                                                        <Text strong style={{ color: '#faad14' }}>
                                                            ${analisisVecinos.resumen.noVecinos.totalDescuentos.toLocaleString('es-CL')}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

export default ReportesDemograficos;
